import React, { useEffect, useState } from "react";
import {
  TextField,
  Checkbox,
  FormControlLabel,
  Button,
  Container,
  Typography,
  Grid,
  Box,
  Alert,
  CircularProgress,
} from "@mui/material";
import "../../TicketEasy.css";
import { useTicketTypeConfigPostMutation } from "../../../../services/ticketEasyService/ticketEasy-api";
import { useAppSelector } from "../../../../app/hooks";
import { useDispatch } from "react-redux";
import { TicketTypeConfigPostCommand } from "../../../../services/ticketEasyService/model/ticketEasy/ticketTypeConfig/TicketTypeConfigPostCommand";
import { setTicketTypeConfig } from "../../../../services/ticketEasyService/model/ticketEasy/ticketTypeConfig/TicketTypeConfigSlice";
import { usePermissions } from "../../hooks/usePermissions";
import { TicketTypeSelect } from "./TicketType/TicketTypeSelect";
import { TicketModuleSelect } from "./TicketModule/TicketModuleSelect";
import { CpsiEmailInput } from "./TicketType/CpsiEmailInput";

const Administration: React.FC = () => {
  const dispatch = useDispatch();
  const ticketTypeConfigState = useAppSelector(state => state.ticketTypeConfig.ticketTypeConfig);
  const selectedModule = useAppSelector(state => state.ticketEasyModule.TicketConfigSelectedModule);
  const [showJobTitleSearch, setShowJobTitleSearch] = useState<boolean>(ticketTypeConfigState.jobTitleMapIds.length > 0);
  const [selectedJobTitle, setSelectedJobTitle] = useState<string | null>(null);
  const { isAdmin, userProfile, isLoading: profileLoading, error: profileError } = usePermissions();

  useEffect(() => {
    dispatch(setTicketTypeConfig(ticketTypeConfigState));
    setShowJobTitleSearch(ticketTypeConfigState.jobTitleMapIds.length > 0);
  }, [ticketTypeConfigState, selectedModule, dispatch]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    dispatch(setTicketTypeConfig({
      ...ticketTypeConfigState,
      [name]: value
    }));
  };

  const [postTicketTypeConfig, { isLoading, isSuccess }] = useTicketTypeConfigPostMutation();
  const command = new TicketTypeConfigPostCommand(ticketTypeConfigState);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!isAdmin) {
      alert("You do not have permission to perform this action.");
      return;
    }
    
    postTicketTypeConfig(command)
      .unwrap()
      .then(() => {
        alert("Ticket configuration saved successfully!");
      })
      .catch((error) => {
        alert("Failed to save ticket configuration: " + error.message);
      });
  };

  if (profileLoading) {
    return (
      <Container>
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (profileError) {
    return (
      <Container>
        <Alert severity="error">
          Error loading user profile. Please refresh the page and try again.
        </Alert>
      </Container>
    );
  }

  if (!isAdmin) {
    return (
      <Container>
        <Alert severity="warning">
          You do not have permission to access the Administration section.
        </Alert>
      </Container>
    );
  }

  return (
    <Container>
      <Typography variant="h4" gutterBottom>
        Administration
      </Typography>
      <Typography variant="body1" gutterBottom>
        Ticket Configuration
      </Typography>
      <Box sx={{ border: '1px solid #ccc', borderRadius: 3, padding: 2, margin: 2 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12} container alignItems="center" spacing={1}>
              <TicketModuleSelect />
            </Grid>
            <Grid item xs={12} container alignItems="center" spacing={1}>
              <TicketTypeSelect />
            </Grid>
            <Grid item xs={12}>
              <CpsiEmailInput visible={ticketTypeConfigState.ticketType === "CPSI"} />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Description"
                name="description"
                value={ticketTypeConfigState.description}
                onChange={handleChange}
                required
                fullWidth
              />
            </Grid>              
            <Grid item xs={12}>
              <Grid container spacing={2} direction="row">
                <Grid item>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="requiresApprovalTypeManager"
                        checked={ticketTypeConfigState.requiresApprovalType.some(type => type.name === "Manager")}
                        onChange={(e) => {
                          const updatedTypes = e.target.checked
                            ? [...ticketTypeConfigState.requiresApprovalType, { name: "Manager" }]
                            : ticketTypeConfigState.requiresApprovalType.filter(type => type.name !== "Manager");
                          dispatch(setTicketTypeConfig({
                            ...ticketTypeConfigState,
                            requiresApprovalType: updatedTypes,
                          }));
                        }}
                      />
                    }
                    label="Manager Approvers"
                  />
                </Grid>
                <Grid item>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="requiresApprovalTypeDepartment"
                        checked={ticketTypeConfigState.requiresApprovalType.some(type => type.name === "Department")}
                        onChange={(e) => {
                          const updatedTypes = e.target.checked
                            ? [...ticketTypeConfigState.requiresApprovalType, { name: "Department" }]
                            : ticketTypeConfigState.requiresApprovalType.filter(type => type.name !== "Department");
                          dispatch(setTicketTypeConfig({
                            ...ticketTypeConfigState,
                            requiresApprovalType: updatedTypes,
                          }));
                        }}
                      />
                    }
                    label="Department Approvers"
                  />
                </Grid>
                <Grid item>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="requiresApprovalTypeFacility"
                        checked={ticketTypeConfigState.requiresApprovalType.some(type => type.name === "Facility")}
                        onChange={(e) => {
                          const updatedTypes = e.target.checked
                            ? [...ticketTypeConfigState.requiresApprovalType, { name: "Facility" }]
                            : ticketTypeConfigState.requiresApprovalType.filter(type => type.name !== "Facility");
                          dispatch(setTicketTypeConfig({
                            ...ticketTypeConfigState,
                            requiresApprovalType: updatedTypes,
                          }));
                        }}
                      />
                    }
                    label="Facility Approvers"
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12}>
            <FormControlLabel
                    control={
                      <Checkbox
                        name="requiresJobTitleMapping"
                        checked={ticketTypeConfigState.requiresApprovalType.some(type => type.name === "JobTitleMapping")}
                        onChange={(e) => {
                          const updatedTypes = e.target.checked
                            ? [...ticketTypeConfigState.requiresApprovalType, { name: "JobTitleMapping" }]
                            : ticketTypeConfigState.requiresApprovalType.filter(type => type.name !== "JobTitleMapping");
                          dispatch(setTicketTypeConfig({
                            ...ticketTypeConfigState,
                            requiresApprovalType: updatedTypes,
                          }));
                        }}
                      />
                    }
                    label="Include Job Title Mapping"
                  />
              </Grid>
            <Grid item xs={12}>
              <Box textAlign="right">
                <Button 
                  type="submit" 
                  variant="contained" 
                  color="primary" 
                  disabled={isLoading || !isAdmin}
                >
                  {isLoading ? "Saving..." : "Save"}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Box>     
    </Container>
  );
};

export { Administration };
